import { AxiosRequestConfig } from "axios";
import { FC, useEffect, useMemo, useState } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import { useRecoilValue } from "recoil";
import * as csv from "csvtojson";
import {
    downloadMultiPharmacyImportTemplate,
    getMultiPharmacyReadPresignedUrl,
    getMultiPharmacyRequiredHeaders,
    getMultiPharmacyUploadPresignedUrl,
    importMultiPharmacyInventoryData,
    uploadFileToS3,
} from "../../../api/inventoryImport";
import { PharmacyModel } from "../../../models";
import { ErrorResponse } from "../../../models/error";
import { accountDataState } from "../../../recoil/account";
import { SearchPharmacys } from "../../Pharmacys";

const defaultRequiredHeaders = ["ID sản phẩm", "Loại đóng gói", "Giá", "Tồn kho"];

const ImportMultiPharmacyInventoryUpload: FC = () => {
    const accountInfo = useRecoilValue(accountDataState);
    const adminId = useMemo(() => {
        if (accountInfo.loginSession.userLocalType === "ADMIN") {
            return accountInfo.doctor.mId;
        }
    }, [accountInfo]);

    const [selectedPharmacies, setSelectedPharmacies] = useState<PharmacyModel[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [requiredHeaders, setRequiredHeaders] = useState<string[]>(defaultRequiredHeaders);
    const [errors, setErrors] = useState<{ message: string }[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDownloadingTemplate, setIsDownloadingTemplate] = useState<boolean>(false);
    const [loadingPercent, setLoadingPercent] = useState<number>(0);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [csvInfo, setCsvInfo] = useState<{
        headers: string[],
        rows: any[],
        errors: string[]
    }>({
        headers: [],
        rows: [],
        errors: []
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!adminId) {
                    return;
                }

                const result = await getMultiPharmacyRequiredHeaders(parseInt(adminId));
                setRequiredHeaders(result.data);
            } catch (err) {
                console.error("cannot fetch multi pharmacy required headers: ", err);
            }
        };

        fetchData();
    }, [adminId]);

    const handleSelectPharmacy = (selectedPharmacy: PharmacyModel) => {
        if (!selectedPharmacy.mPharCode) {
            setErrors([{ message: "Nhà thuốc chưa có mã phar_code" }]);
            return;
        }

        setErrors([]);
        setSelectedPharmacies(prev => {
            if (prev.some(pharmacy => pharmacy.mPharCode === selectedPharmacy.mPharCode)) {
                return prev;
            }

            return [...prev, selectedPharmacy];
        });
    };

    const removeSelectedPharmacy = (pharCode: string) => {
        setSelectedPharmacies(prev => prev.filter(pharmacy => pharmacy.mPharCode !== pharCode));
    };

    const onFileChange = (files: FileList | null) => {
        if (!files || files.length === 0) {
            return;
        }

        let file = files[0];

        setSelectedFile(file);
        setIsSuccess(false);
        setErrors([]);

        const reader = new FileReader();
        reader.onload = (e) => {
            if (e.target) {
                const text = e.target.result;
                if (text && typeof text === "string") {
                    processCSV(text);
                }
            }
        };
        reader.readAsText(file);
    };

    const processCSV = async (str: string) => {
        const rows = await csv.default().fromString(str);
        let headers: string[] = [];

        if (rows && rows.length > 0) {
            for (let key in rows[0]) {
                headers.push(key);
            }
        }

        let errors = [];

        if (!isValidHeader(headers)) {
            errors.push(`Cần có đủ các cột: ${requiredHeaders.join(", ")}`);
        }

        if (!rows || rows.length === 0) {
            errors.push("Không tìm thấy dữ liệu, vui lòng kiểm tra lại");
        }

        setCsvInfo({
            headers,
            rows,
            errors
        });
    };

    const FileErrors = () => {
        let maps = csvInfo.errors.map((error, index) => {
            return <div className="alert alert-danger" key={index}>
                <img alt="none" src="./images/report_problem.svg" title="Error" className="img-fluid" width="20" />
                <span className="ml-2">{error}</span>
            </div>;
        });

        return <div>{maps}</div>;
    };

    const ServerErrors = () => {
        let maps = errors.map((error, index) => {
            return <div className="alert alert-danger" key={index}>
                <img alt="none" src="./images/report_problem.svg" title="Error" className="img-fluid" width="20" />
                <span className="ml-2">{error.message}</span>
            </div>;
        });

        return <div>{maps}</div>;
    };

    const isValidHeader = (headers: string[]) => {
        for (let col of requiredHeaders) {
            let temp = headers.includes(col);
            if (!temp) {
                return false;
            }
        }
        return true;
    };

    const getDisableClass = () => {
        if (
            isLoading
            || !selectedFile
            || selectedPharmacies.length === 0
            || csvInfo.errors.length > 0
        ) {
            return "disabled";
        }

        return "";
    };

    const clearSelectedFile = () => {
        setSelectedFile(null);
        let element = document.getElementById("multiPharmacyFileInp") as HTMLInputElement;
        if (element) {
            element.value = "";
        }
    };

    const onDownloadTemplateClick = async () => {
        if (!adminId || isDownloadingTemplate) {
            return;
        }

        setIsDownloadingTemplate(true);
        setErrors([]);

        try {
            const response = await downloadMultiPharmacyImportTemplate(parseInt(adminId));
            const disposition = response.headers["content-disposition"];
            let filename = "multi-pharmacy-import-template.csv";

            if (disposition && disposition.includes("filename=")) {
                filename = disposition.split("filename=")[1].replace(/"/g, "").trim();
            }

            const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8;" }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setErrors([{ message: "Không thể tải file template, vui lòng thử lại" }]);
        } finally {
            setIsDownloadingTemplate(false);
        }
    };

    const onUploadClick = async () => {
        if (
            isLoading
            || csvInfo.errors.length > 0
            || selectedPharmacies.length === 0
            || !adminId
            || !selectedFile
        ) {
            return;
        }

        setIsLoading(true);
        setErrors([]);

        try {
            const pharCodes = selectedPharmacies.map(pharmacy => pharmacy.mPharCode);
            let response = await getMultiPharmacyUploadPresignedUrl(parseInt(adminId), selectedFile.name, pharCodes);

            var options: AxiosRequestConfig = {
                headers: {
                    "Content-Type": "text/csv"
                },
                onUploadProgress: (progressEvent) => {
                    setLoadingPercent(Math.round(progressEvent.loaded / progressEvent.total * 100));
                }
            };

            await uploadFileToS3(response.data.preSignedUrl, selectedFile, options);

            let readFilePSUReponse = await getMultiPharmacyReadPresignedUrl(parseInt(adminId), response.data.key);

            await importMultiPharmacyInventoryData(parseInt(adminId), readFilePSUReponse.data.preSignedUrl, selectedFile.name, pharCodes);

            setIsSuccess(true);
            setIsLoading(false);

            clearSelectedFile();
        } catch (err: any) {
            setIsLoading(false);
            if (err.response && err.response.data) {
                let errorRes: ErrorResponse = err.response.data;
                if (errorRes.error.details) {
                    setErrors(errorRes.error.details.map(errItem => {
                        if (errItem.vi) {
                            return { message: errItem.vi.message };
                        }
                        return { message: errItem.message };
                    }));
                } else {
                    setErrors([{ message: errorRes.error.message }]);
                }
            } else {
                setErrors([{ message: "Có lỗi xảy ra, vui lòng thử lại" }]);
            }
        }
    };

    return (
        <div>
            <LoadingOverlay
                active={isLoading}
                spinner
                text={"Đang xử lý ... " + loadingPercent + "%"}
                fadeSpeed={500}
            >
                {
                    isLoading ?
                        <div className="overlay-wrapper">
                        </div>
                        : ""
                }
            </LoadingOverlay>
            <SearchPharmacys handleSelect={handleSelectPharmacy} />
            <div className="management-content">
                <div className="row mt-3 mb-4 align-items-center">
                    <div className="col-auto">
                        File mẫu
                    </div>
                    <div className="col-auto ml-auto">
                        <button
                            className="btn px-3"
                            disabled={isDownloadingTemplate}
                            onClick={onDownloadTemplateClick}
                        >
                            {isDownloadingTemplate ? "Đang tải..." : "Tải template"}
                        </button>
                    </div>
                </div>

                <div className="row mt-3 mb-4 align-items-start">
                    <div className="col-auto">
                        Nhà thuốc đã chọn
                    </div>
                    <div className="col ml-auto text-right">
                        {
                            selectedPharmacies.length > 0 ?
                                selectedPharmacies.map(pharmacy => (
                                    <div className="mb-2" key={pharmacy.mPharCode}>
                                        <strong>{pharmacy.mName}</strong>
                                        <span className="ml-2 txt-gray">{pharmacy.mPharCode}</span>
                                        <a
                                            href="/#"
                                            className="ml-2 text-danger"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                removeSelectedPharmacy(pharmacy.mPharCode);
                                            }}
                                        >
                                            Xóa
                                        </a>
                                    </div>
                                ))
                                : "Chưa có"
                        }
                    </div>
                </div>

                {
                    selectedPharmacies.length > 0 ?
                        <div className="form-group form-file">
                            <input type="file" id="multiPharmacyFileInp" name="img" accept=".csv" onChange={(e) => onFileChange(e.target.files)} />
                            <span className="input-label"><img alt="none" src="./images/MDI-upload.svg" title="Tải lên" className="img-fluid" width="24" /> <span>Tải File/Upload kho nhiều NT</span></span>
                        </div>
                        : <div className="form-group form-file">
                            <span className="input-label"><span>Vui lòng chọn nhà thuốc</span></span>
                        </div>
                }

                {
                    selectedFile ?
                        <div>
                            <p>Tên file: <span>{selectedFile.name}</span></p>
                            <p>Số lượng sản phẩm: {new Intl.NumberFormat().format(csvInfo.rows.length)}</p>
                            <p>Số nhà thuốc: {new Intl.NumberFormat().format(selectedPharmacies.length)}</p>
                            <FileErrors />
                            <ServerErrors />
                        </div>
                        : <ServerErrors />
                }
                {
                    isSuccess ?
                        <div className="text-center mt-5 text-success">
                            Tải dữ liệu thành công, <br />vui lòng xem chi tiết ở lịch sử tải kho.
                        </div> : ""
                }

                <div className="fixed-button">
                    <a onClick={
                        (e) => {
                            e.preventDefault();
                            onUploadClick();
                        }
                    } className={`btn btn-block ${getDisableClass()}`} href="/#">XỬ LÝ FILE</a>
                </div>
            </div>
        </div>
    );
};

export default ImportMultiPharmacyInventoryUpload;
